import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Chip,
  Grid,
} from '@mui/material';
import {
  GridOn as GridIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { getScoreColor } from '../../theme';

const IndicatorHeatmap = ({ showAIBadges = true }) => {
  const { t } = useTranslation();
  const [indicators, setIndicators] = useState([]);

  useEffect(() => {
    fetchIndicators();
  }, []);

  const fetchIndicators = async () => {
    try {
      const response = await api.get('/macro/indicators');
      setIndicators(response.data || []);
    } catch (error) {
      console.error('Error fetching indicators:', error);
    }
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return t('common.strong');
    if (score >= 60) return t('common.good');
    if (score >= 50) return t('common.neutral');
    if (score >= 40) return t('common.weak');
    return t('common.veryWeak');
  };

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        mb: 3,
        overflow: 'visible',
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'background.paper',
          boxShadow: 'none',
        },
        transition: 'none',
      }}
    >
      {/* Header with gradient - matching AI News Impact style */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {/* Left: Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <GridIcon sx={{ fontSize: 28, mr: 1.5 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('dashboard.indicatorHeatmap.title')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t('dashboard.indicatorHeatmap.subtitle')}
              </Typography>
            </Box>
          </Box>

          {/* Right: Summary Stats */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {/* Tổng Số */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                {t('dashboard.indicatorHeatmap.totalIndicators')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {indicators.length}
              </Typography>
            </Box>

            {/* Có AI */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                {t('dashboard.indicatorHeatmap.withAI')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {indicators.filter(i => i.uses_ai_adjustment).length}
              </Typography>
            </Box>

            {/* Trung Bình */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                {t('dashboard.indicatorHeatmap.avgScore')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {(indicators.reduce((sum, i) => sum + (i.ai_adjusted_score || i.base_score || 0), 0) / indicators.length).toFixed(1)}
              </Typography>
            </Box>

            {/* Mạnh */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                {t('dashboard.indicatorHeatmap.strongIndicators')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {indicators.filter(i => (i.ai_adjusted_score || i.base_score || 0) >= 70).length}
              </Typography>
            </Box>

            {/* AI Badge */}
            {showAIBadges && (
              <Chip
                icon={<AIIcon />}
                label={t('dashboard.indicatorHeatmap.aiEnabled')}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <CardContent sx={{ p: 2 }}>

        {/* Color Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { color: '#11998e', label: '≥70 Strong' },
            { color: '#667eea', label: '60-69 Good' },
            { color: '#ffa726', label: '50-59 Neutral' },
            { color: '#ff9800', label: '40-49 Weak' },
            { color: '#ff6b6b', label: '<40 Very Weak' },
          ].map((item) => (
            <Box key={item.color} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: item.color, borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Heatmap Grid */}
        <Grid container spacing={1}>
          {indicators.map((indicator) => {
            const score = indicator.ai_adjusted_score || indicator.base_score || 0;
            const hasAI = indicator.uses_ai_adjustment;
            const adjustment = indicator.ai_adjustment_factor || 0;
            const scoreColor = getScoreColor(indicator.indicator_score_level);

            return (
              <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={indicator.indicator_id}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        {indicator.indicator_name}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        {t('dashboard.indicatorHeatmap.score')}: {score.toFixed(1)}
                      </Typography>
                      {hasAI && showAIBadges && (
                        <>
                          <Typography variant="caption" sx={{ display: 'block', color: '#667eea' }}>
                            🤖 {t('dashboard.indicatorHeatmap.aiAdjusted')}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            {t('dashboard.indicatorHeatmap.adjustment')}: {(adjustment * 100).toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            {t('dashboard.indicatorHeatmap.baseScore')}: {indicator.base_score?.toFixed(1)}
                          </Typography>
                        </>
                      )}
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        {getScoreLabel(score)}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      position: 'relative',
                      p: 1.5,
                      bgcolor: scoreColor,
                      color: 'white',
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    {/* AI Badge */}
                    {hasAI && showAIBadges && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#667eea',
                          border: '2px solid white',
                          boxShadow: 1,
                        }}
                      />
                    )}

                    {/* Indicator Code */}
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        display: 'block',
                        textAlign: 'center',
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {indicator.indicator_id}
                    </Typography>

                    {/* Score */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                        fontSize: '1rem',
                      }}
                    >
                      {score.toFixed(0)}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default IndicatorHeatmap;

